import { ref, inject } from "vue";
import type { ShallowRef, Component, InjectionKey } from "vue";
import { createSharedComposable } from "@vueuse/core";
import type { ComponentProps, Dialog, DialogState } from "../types/baseComponents";

export const dialogInjectionKey: InjectionKey<ShallowRef<DialogState>> = Symbol( "apollo.dialog" );

function _useDialogs () {
  const dialogState = inject( dialogInjectionKey );

  const isOpen = ref( false );

  function open<T extends Component> ( component: T, props?: Dialog & ComponentProps<T> ) {
    if ( !dialogState ) {
      throw new Error( "useDialog() is called without provider" );
    }

    dialogState.value = {
      component,
      props: props ?? {},
    };

    isOpen.value = true;
  }

  function close () {
    if ( !dialogState ) {
      return;
    }

    isOpen.value = false;
  }

  function reset () {
    if ( !dialogState ) {
      throw new Error( "useDialog() is called without provider" );
    }
    dialogState.value = {
      component: "div",
      props: {},
    };
  }

  /**
   * Allows updating the modal props
   */
  function patch<T extends Component = {}> ( props: Partial<Dialog & ComponentProps<T>> ) {
    if ( !dialogState ) {
      return;
    }

    dialogState.value = {
      ...dialogState.value,
      props: {
        ...dialogState.value.props,
        ...props,
      },
    };
  }

  return {
    open,
    close,
    reset,
    patch,
    isOpen,
  };
}

export const useDialogs = createSharedComposable( _useDialogs );
